// Modules
import { Children, useEffect, useRef, useState } from 'react';
import cn from 'classnames';

// Types
import type { JSX } from 'react';
import type { TTabActive, TTabListProps } from './Tabs.type';

// Styles
import styles from './styles/styles.module.scss';

// Components
import Tab from './Tab';

// Helpers
import { getTabsIndicator } from './Tabs.helper';
import { isFunction } from '../../helpers/typeChecker';

// Constants
import {
	TAB_ACTIVE_STYLE_DEFAULT,
	TAB_DISPLAY_NAME,
	TAB_LIST_DEFAULT_PROPS,
	UNKNOWN_TAB_ACTIVE
} from './Tab.constant';

const TabList = (props: TTabListProps) => {
	const {
		activeTab = TAB_LIST_DEFAULT_PROPS.activeTab,
		children,
		className,
		onChange,
		position = TAB_LIST_DEFAULT_PROPS.position,
		...restProps
	} = props;

	const tabListCN = cn(styles.legion_tab_list, className, styles[position!]);
	const tabChildrenRef = useRef<(HTMLDivElement | null)[]>([]);
	const [activeTabStyle, setActiveTabStyle] = useState<TTabActive>(TAB_ACTIVE_STYLE_DEFAULT);
	const [disabledTabs, setDisabledTabs] = useState<number[]>([]);

	const handleChangeTab = (tabIndex: number) => {
		if (tabIndex >= 0 && tabIndex < tabChildrenRef.current.length && !disabledTabs.includes(tabIndex)) {
			setActiveTabStyle({
				index: tabIndex,
				...getTabsIndicator(position!, tabChildrenRef.current[tabIndex])
			})
		} else {
			setActiveTabStyle({
				...activeTabStyle,
				...UNKNOWN_TAB_ACTIVE,
				index: tabIndex
			})
		}

		if (isFunction(onChange)) {
			onChange(tabIndex)
		}
	}

	useEffect(() => {
		const disableds: number[] = [];
		Children.toArray(children).forEach((child, childIndex) => (child as JSX.Element)?.props?.disabled && disableds.push(childIndex))

		if (!disableds.includes(activeTab!)) {
			setActiveTabStyle({
				...activeTabStyle,
				...getTabsIndicator(position!, tabChildrenRef.current[activeTabStyle.index])
			})
		} else {
			setActiveTabStyle({
				...activeTabStyle,
				...UNKNOWN_TAB_ACTIVE,
				index: activeTab!
			})
		}

		setDisabledTabs(disableds)
	}, [])

	useEffect(() => {
		handleChangeTab(activeTab!)
	}, [activeTab])

	return (
		<div className={tabListCN} {...restProps}>
			{
				// Filter out children that are not `Tab` components
				Children.toArray(children).filter(child => (child as JSX.Element)?.type?.displayName === TAB_DISPLAY_NAME).map((child, childIndex) => {
					const { className, key, onClick, ...restProps } = (child as JSX.Element).props
					const childKey = key || childIndex;

					const childProps = {
						...restProps,
						ref: (el: HTMLDivElement) => tabChildrenRef.current[childIndex] = el,
						className: cn(className, styles.tab),
						onClick: () => {
							if (restProps.disabled) return;
							isFunction(onClick) && onClick();
							handleChangeTab(childIndex)
						},
						active: activeTabStyle.index === childIndex,
						style: {
							...restProps.style,
						},
						position
					}

					return (
						<Tab key={childKey} {...childProps} />
					)
				})
			}
			<span
				className={styles.tab_indicator}
				style={{ ...activeTabStyle }}
			/>
		</div>
	)
}

export default TabList;
